import type {CourseThemeDocument} from 'course/models/courseThemeDocument';

/**
 * PAI's light course theme — the deploy's default look and feel, injected into
 * CIP's generic theming system via `AppOverrides.courseTheme` (see `main.tsx`).
 * It is a plain {@link CourseThemeDocument}, identical in shape to a per-course
 * Firestore theme doc, so it carries no PAI-specific coupling into `webapp`;
 * only the values here are PAI-specific.
 *
 * Colors follow the PAI splash (`splash/HeroSplash.tsx`):
 * Stanford cardinal for the navbar and primary actions, a warm off-white body
 * with white surfaces, and near-black text. Typography is Source Sans 3 — a
 * clean, highly readable humanist sans (Adobe's open-source family, SIL OFL;
 * also Coursera's brand font). It is loaded remotely from Google Fonts via
 * {@link CourseThemeDocument.remoteFontUrls} for zero-install iteration; for
 * production, self-host it via `@fontsource/source-sans-3` (as Jost is in
 * `main.tsx`) and drop the remote URL.
 */
export const PAI_COURSE_THEME: Partial<CourseThemeDocument> = {
  // Colors: cardinal chrome on a warm off-white page.
  navbarColor: '#8c1515',
  buttonColorPrimary: '#8c1515',
  linkColor: '#8c1515',
  accentColor: '#8c1515',
  bodyBackgroundColor: '#fafaf9',
  bodyTextColor: '#111111',
  surfaceColor: '#ffffff',
  surfaceTextColor: '#111111',
  // Load the font family remotely (no dependency install) while we iterate.
  remoteFontUrls: [
    'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&display=swap',
    'https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400&display=swap',
  ],
  // The course name in the navbar uses the splash hero's title face (Jost 700,
  // self-hosted app-wide in main.tsx); on phones the full name does not fit.
  navbarShortTitle: 'PAI',
  brandName: 'Probability for AI',
  brandFontFamily: 'Jost',
  brandFontWeight: 700,
  // Fonts: Source Sans 3 everywhere — body and headings.
  mainBodyFontFamily: 'Source Sans 3',
  h1FontFamily: 'Source Sans 3',
  h2FontFamily: 'Source Sans 3',
  h3FontFamily: 'Source Sans 3',
  h4FontFamily: 'Source Sans 3',
  h5FontFamily: 'Source Sans 3',
};

/**
 * PAI's dark scheme. Passed as `AppOverrides.courseThemeDark`, it is overlaid
 * on {@link PAI_COURSE_THEME} when a user's resolved color scheme is dark and
 * turns on the color-scheme setting (see `ColorSchemeProvider` in `webapp`).
 * Only color fields differ, so fonts and
 * brand come from the light theme. A dark grey page rather than pure black
 * keeps contrast comfortable; links and the accent use the hero's lighter pi
 * red, since cardinal itself falls below 3:1 contrast on a dark background.
 */
export const PAI_COURSE_THEME_DARK: Partial<CourseThemeDocument> = {
  navbarColor: '#8c1515',
  buttonColorPrimary: '#8c1515',
  linkColor: '#db6b5c',
  accentColor: '#db6b5c',
  bodyBackgroundColor: '#121212',
  bodyTextColor: '#e6e6e6',
  surfaceColor: '#1e1e1e',
  surfaceTextColor: '#e6e6e6',
};
